import { addDatabaseChangeListener } from "@-/com.npmjs.expo-sqlite";
import type { Query, QueryClient, QueryKey } from "@-/com.npmjs.tanstack__react-query";
import { hashKey, useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { queueCoalescedInvalidations } from "#queue-coalesced-invalidations";

/**
 * The committed-write event delivered to a {@link DatabaseChangePredicate}.
 * Mirrors the payload of `addDatabaseChangeListener` (both the native and the
 * web manual feeds), which is database-agnostic: every connection that enables
 * the change listener routes through the same emitter, so this serves the actor
 * and device databases alike.
 *
 * NB: `rowId` is SQLite's implicit integer `rowid`, *not* the row's `id`
 * column, and on the actor database it is only the connection's
 * `lastInsertRowId` (see `buffered-manual-change-feed.ts`): an UPDATE, DELETE
 * or RETURNING statement reports some other row or 0. Match actor events by
 * `tableName`; a predicate that maps `rowId` back to an application key misses
 * those writes and leaves its query stale.
 */
export type DatabaseChangeEvent = {
  databaseFilePath: string;
  databaseName: string;
  rowId: number;
  tableName: string;
};

/**
 * Predicate deciding whether a given change event is relevant to a registered
 * query key.
 *
 * Matching is expressed in code rather than as a static table list, so a
 * predicate can narrow on more than `tableName` (e.g. `rowId`). It may be
 * **async**: the key only invalidates once the returned promise resolves to
 * `true`. Because a change event fires *after* its write commits, an async
 * predicate that reads the just-committed state and then invalidates is
 * race-free — the only consequence is that the resulting refetch is scheduled a
 * tick/frame after the write instead of in the same tick. A *synchronous*
 * predicate returning `true` still invalidates in the same tick.
 */
export type DatabaseChangePredicate = (event: DatabaseChangeEvent) => boolean | Promise<boolean>;

/**
 * Wraps a consumer's `queryKey`: returns it **verbatim** (no version segment
 * appended) while registering it as an invalidation target for
 * the predicate bound by {@link useTableChangeVersion}. Whenever a committed
 * write matches the predicate, every cached key passed through this wrapper is
 * invalidated (`invalidateQueries({ queryKey, exact: true })`), so those
 * queries refetch — freshness derived from invalidation rather than a rotating
 * key or `staleTime`.
 *
 * Call it inside a `queryOptions` `get queryKey()` (or a key getter), passing
 * the exact key the query is registered under.
 */
export type WithTableChangeVersion = <TQueryKey extends QueryKey>(queryKey: TQueryKey) => TQueryKey;

export type DatabaseQueryRegistration = {
  predicate: DatabaseChangePredicate;
  queryClient: QueryClient;
  removed: boolean;
  tables: readonly string[];
};

// The table dependencies a predicate stands for, recorded by `tableNameMatcher`
// so a key registered through the wrapper carries them without the consumer
// restating the list. Row-scoped predicates (which narrow on `rowId`) can't
// advertise their tables this way and pass them explicitly to
// `useTableChangeVersion` instead.
const tablesByPredicate = new WeakMap<DatabaseChangePredicate, readonly string[]>();

function readAttachedTables(predicate: DatabaseChangePredicate): readonly string[] {
  return tablesByPredicate.get(predicate) ?? [];
}

/**
 * Resolve the registration for the actual query lifetime, not a process-global hash.
 * The key-only form supports standalone guarded reads; normal Query functions also pass
 * their client, which disambiguates equal keys used by independent caches.
 */
export function getDatabaseQueryRegistration(context: {
  client?: QueryClient;
  queryKey: QueryKey;
}): DatabaseQueryRegistration | undefined {
  const query = context.client?.getQueryCache().get(hashKey(context.queryKey));
  const registration =
    query === undefined
      ? getKeyRegistration(context.queryKey, context.client)
      : registrationsByQuery.get(query);
  return registration?.removed === false ? registration : undefined;
}

/** Freshness checks receive a Query object and must retain that cache's ownership. */
export function getCachedDatabaseQueryRegistration(query: Pick<Query, "queryHash" | "queryKey">) {
  return registrationsByQuery.get(query);
}

// An inactive query still needs invalidation until QueryCache removes it. Component unmounts
// and re-renders therefore do not release registrations. Cache removal DOES: retaining every
// historical hash pinned predicates, their captured grapheme sets and whole QueryClients after
// Query's own GC. Equal hashes from different clients also overwrote one another.
const registrationsByQuery = new Map<
  Pick<Query, "queryHash" | "queryKey">,
  DatabaseQueryRegistration
>();
// A getter can run in an abandoned render without ever constructing a Query. Weak keys keep
// that preparatory metadata available to a subsequent fetch without making it a global root.
const registrationsByKey = new WeakMap<QueryKey, DatabaseQueryRegistration>();
const registrationsByKeyAndClient = new WeakMap<
  QueryKey,
  WeakMap<QueryClient, DatabaseQueryRegistration>
>();
const observedClients = new WeakSet<QueryClient>();
let removeListener: (() => void) | null = null;

function getKeyRegistration(queryKey: QueryKey, queryClient?: QueryClient) {
  return queryClient === undefined
    ? registrationsByKey.get(queryKey)
    : registrationsByKeyAndClient.get(queryKey)?.get(queryClient);
}

function setKeyRegistration(queryKey: QueryKey, registration: DatabaseQueryRegistration): void {
  registrationsByKey.set(queryKey, registration);
  let clientsForKey = registrationsByKeyAndClient.get(queryKey);
  if (clientsForKey === undefined) {
    clientsForKey = new WeakMap();
    registrationsByKeyAndClient.set(queryKey, clientsForKey);
  }
  clientsForKey.set(registration.queryClient, registration);
}

function observeQueryCache(queryClient: QueryClient): void {
  if (observedClients.has(queryClient)) {
    return;
  }
  observedClients.add(queryClient);
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type === "added") {
      const metadata = getKeyRegistration(event.query.queryKey, queryClient);
      if (metadata !== undefined) {
        // Reusing old options after GC starts a NEW lifetime, including new freshness proof.
        const registration = { ...metadata, removed: false };
        registrationsByQuery.set(event.query, registration);
        setKeyRegistration(event.query.queryKey, registration);
      }
    } else if (event.type === "removed") {
      const registration = registrationsByQuery.get(event.query);
      if (registration !== undefined) {
        registration.removed = true;
        registrationsByQuery.delete(event.query);
      }
    }
  });
}

function ensureListening(): void {
  if (removeListener !== null) {
    return;
  }
  const subscription = addDatabaseChangeListener((event) => {
    for (const [query, registration] of registrationsByQuery) {
      // Composed keys contain nested options; reuse the hash rather than stringify per write.
      dispatch(query.queryHash, registration, event);
    }
  });
  removeListener = () => {
    subscription.remove();
  };
}

function dispatch(
  queryHash: string,
  registration: DatabaseQueryRegistration,
  event: DatabaseChangeEvent,
): void {
  let result: boolean | Promise<boolean>;
  try {
    result = registration.predicate(event);
  } catch {
    // A throwing predicate must not break the write path — mirror the
    // fire-and-forget swallow of `emitManualDatabaseChange`. Treat as no match.
    return;
  }
  if (result === true && !registration.removed) {
    // Sync match: the coalesced queue delivers the invalidation on the next
    // microtask, so a burst of writes in one window invalidates the key once.
    queueCoalescedInvalidations(registration.queryClient, [queryHash]);
    return;
  }
  if (typeof result === "boolean") {
    return;
  }
  // Async match: invalidate once resolved (relaxed timing). Swallow rejections so
  // a failed predicate is treated as "no match" rather than an unhandled rejection.
  void result.then(
    (matched) => {
      if (matched && !registration.removed) {
        queueCoalescedInvalidations(registration.queryClient, [queryHash]);
      }
    },
    () => {},
  );
}

/**
 * Returns a render-scoped wrapper — {@link WithTableChangeVersion} — to pass the
 * consumer's `queryKey` through. Each key built through the wrapper is
 * registered for its cache lifetime as an invalidation target for `predicate`: every
 * committed write the predicate matches invalidates that key, whether or not
 * the registering component is still mounted and regardless of how many
 * renders have happened since.
 *
 * The wrapper returns the key **unchanged**; the key carries no version
 * segment. This is the only public API: by returning a value the consumer must
 * call to build their `queryKey`, it's impossible to subscribe without
 * registering an invalidation target (or vice versa).
 *
 * Use as:
 *
 * ```ts
 * const withVersion = useTableChangeVersion(tableNameMatcher(TABLES));
 * return queryOptions({
 *   queryFn: …,
 *   get queryKey() {
 *     return withVersion(["…"] as const);
 *   },
 * });
 * ```
 */
export function useTableChangeVersion(
  predicate: DatabaseChangePredicate,
  tables?: readonly string[],
): WithTableChangeVersion {
  const queryClient = useQueryClient();
  const watchedTables = tables ?? readAttachedTables(predicate);

  return (queryKey) => {
    // Registering during render (or later, from prefetch/fetch code reading a
    // key getter) is deliberate and idempotent: re-registering the same
    // semantic key replaces its entry with the freshest predicate/client, and
    // an abandoned concurrent render registers the same key a committed render
    // would — invalidating it can never target an uncommitted identity.
    ensureListening();
    observeQueryCache(queryClient);
    const query = queryClient.getQueryCache().get(hashKey(queryKey));
    const registration = (query === undefined ? undefined : registrationsByQuery.get(query)) ?? {
      predicate,
      queryClient,
      removed: false,
      tables: watchedTables,
    };
    registration.predicate = predicate;
    registration.tables = watchedTables;
    setKeyRegistration(queryKey, registration);
    if (query !== undefined) {
      setKeyRegistration(query.queryKey, registration);
      registrationsByQuery.set(query, registration);
    }
    return queryKey;
  };
}

/**
 * Builds a {@link DatabaseChangePredicate} that matches any change to one of
 * `tables` (by `tableName`). The internal `Set` gives O(1) matching and lets a
 * `readonly [...] as const` tuple be passed without a cast (a tuple's
 * `.includes` is typed to its literal members and rejects an arbitrary string).
 */
export function tableNameMatcher(tables: readonly string[]): DatabaseChangePredicate {
  const tableSet = new Set<string>(tables);
  const predicate: DatabaseChangePredicate = (event) => tableSet.has(event.tableName);
  // Record the list so a key registered through the wrapper declares its database
  // dependencies to the write-clock freshness check without restating them.
  tablesByPredicate.set(predicate, [...tables]);
  return predicate;
}
